import React, { useRef, useState, useMemo } from 'react';
import { DeleteIcon } from './icons/DeleteIcon';
import { RefreshIcon } from './icons/RefreshIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { Search, Film, Plus, FolderOpen, Clock, Clapperboard, ChevronLeft, ChevronRight, Coins, ImagePlus } from 'lucide-react';

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/997f7140-85c2-448c-99ca-589fbe4a3293/images/19298b51b6f55b28b4d90775848476236671ffb7e18465c0d08fabe4255bcbda.png';

const PLACEHOLDER_THUMBS = [
    'https://static.prod-images.emergentagent.com/jobs/997f7140-85c2-448c-99ca-589fbe4a3293/images/92ff20b7f71a7cf263adec25d73f6047192e62ce31d32abb732f9413686f65f2.png',
    'https://static.prod-images.emergentagent.com/jobs/997f7140-85c2-448c-99ca-589fbe4a3293/images/274a11074255e51f9580e7342e13d0cb42e30d046d5e46bdfd4ad5be4d787f38.png',
    'https://static.prod-images.emergentagent.com/jobs/997f7140-85c2-448c-99ca-589fbe4a3293/images/f34c14dc59907d148a2728b177ff1b8220b532bffdbd91602ae765511c32c405.png',
];

const ITEMS_PER_PAGE = 10;

const formatDate = (timestamp) => {
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    }).format(new Date(timestamp));
};

const formatRelative = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return formatDate(timestamp);
};

export const ProjectDashboard = ({ projects, onCreate, onLoad, onDelete, onImport, onRefresh, isLoading, userName, credits, onUpdateThumbnail }) => {
    const [selectedId, setSelectedId] = useState(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const thumbInputRef = useRef(null);

    const filtered = useMemo(() => {
        if (!search.trim()) return projects;
        const q = search.toLowerCase();
        return projects.filter(p => p.name?.toLowerCase().includes(q));
    }, [projects, search]);

    // Reset to page 1 when search changes
    useMemo(() => setPage(1), [search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    const selectedProject = projects.find(p => p.id === selectedId) || null;
    const getThumb = (index) => PLACEHOLDER_THUMBS[index % PLACEHOLDER_THUMBS.length];

    return (
        <div className="w-full h-full overflow-y-auto" style={{ background: '#050505', fontFamily: "'Manrope', sans-serif" }}>
            <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-6">

                {/* Hero Banner */}
                <div className="relative w-full rounded-2xl overflow-hidden mb-8 border border-white/10" data-testid="hero-banner">
                    <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
                    <div className="relative z-10 px-8 py-10 md:py-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-400/80 mb-2">Your AI Film Studio</p>
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-tight">
                                Welcome back, {userName}
                            </h1>
                            <p className="text-zinc-400 mt-2 text-sm md:text-base max-w-lg">
                                Create cinematic videos with AI-powered scripts, characters, and storyboards.
                            </p>
                            <div className="flex items-center gap-6 mt-5">
                                <div className="flex items-center gap-2">
                                    <Film className="w-4 h-4 text-zinc-500" />
                                    <span className="text-sm text-zinc-400"><span className="font-bold text-white">{projects.length}</span> Projects</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clapperboard className="w-4 h-4 text-zinc-500" />
                                    <span className="text-sm text-zinc-400"><span className="font-bold text-white">{credits?.toLocaleString() || 0}</span> Credits</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3 shrink-0">
                            <button
                                onClick={onCreate}
                                className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-full px-6 py-2.5 transition-transform hover:-translate-y-0.5 text-sm"
                                data-testid="create-project-btn"
                            >
                                <Plus className="w-4 h-4" strokeWidth={3} />
                                New Project
                            </button>
                        </div>
                    </div>
                </div>

                {/* Search + Refresh bar */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-[#0F1115] border border-white/10 rounded-xl text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500/50 transition-colors"
                            data-testid="search-projects-input"
                        />
                    </div>
                    <button
                        onClick={onRefresh}
                        disabled={isLoading}
                        className="p-2.5 rounded-xl bg-[#0F1115] border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50"
                        title="Refresh projects"
                    >
                        <RefreshIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                    {/* Page info */}
                    {filtered.length > ITEMS_PER_PAGE && (
                        <span className="text-xs text-zinc-500 ml-auto">Page {page} of {totalPages}</span>
                    )}
                </div>

                {/* Main two-column layout */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Left: Project Card Grid */}
                    <div className="lg:col-span-8">
                        {isLoading && projects.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64">
                                <LoadingSpinner className="w-8 h-8 mb-3 text-violet-500" />
                                <p className="text-zinc-500 text-sm">Loading your projects...</p>
                            </div>
                        ) : paginated.length > 0 ? (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="project-grid">
                                    {paginated.map((project, idx) => {
                                        const globalIdx = (page - 1) * ITEMS_PER_PAGE + idx;
                                        const isSelected = selectedId === project.id;
                                        const creditsUsed = project.creditsUsed || 0;
                                        return (
                                            <div
                                                key={project.id}
                                                onClick={() => setSelectedId(project.id)}
                                                className={`group relative rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer hover:-translate-y-1 ${
                                                    isSelected
                                                        ? 'border-violet-500/50 shadow-[0_10px_40px_rgba(245,158,11,0.1)]'
                                                        : 'border-white/10 hover:border-white/20'
                                                }`}
                                                style={{ background: '#0F1115' }}
                                                data-testid={`project-card-${project.id}`}
                                            >
                                                {/* Thumbnail */}
                                                <div className="h-32 overflow-hidden relative">
                                                    <img
                                                        src={project.thumbnail || getThumb(globalIdx)}
                                                        alt={project.name}
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] to-transparent" />
                                                    {isSelected && (
                                                        <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-violet-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
                                                    )}
                                                </div>
                                                {/* Info */}
                                                <div className="p-4">
                                                    <h3 className="font-bold text-white text-sm truncate">{project.name}</h3>
                                                    <div className="flex items-center justify-between mt-1.5">
                                                        <div className="flex items-center gap-2">
                                                            <Clock className="w-3 h-3 text-zinc-600" />
                                                            <span className="text-xs text-zinc-500">{formatRelative(project.lastModified)}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Coins className="w-3 h-3 text-violet-500/60" />
                                                            <span className="text-xs text-violet-500/80 font-semibold">{creditsUsed.toLocaleString()}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            className="p-2 rounded-lg bg-[#0F1115] border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                            <button
                                                key={p}
                                                onClick={() => setPage(p)}
                                                className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${
                                                    p === page
                                                        ? 'bg-violet-500 text-black'
                                                        : 'bg-[#0F1115] border border-white/10 text-zinc-400 hover:text-white hover:border-white/20'
                                                }`}
                                            >
                                                {p}
                                            </button>
                                        ))}
                                        <button
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            disabled={page === totalPages}
                                            className="p-2 rounded-lg bg-[#0F1115] border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : projects.length === 0 ? (
                            <div className="text-center py-20 px-8 flex flex-col items-center rounded-2xl border border-white/10" style={{ background: '#0F1115' }}>
                                <FolderOpen className="w-12 h-12 text-zinc-700 mb-4" />
                                <h2 className="text-xl font-bold text-zinc-300">No projects yet</h2>
                                <p className="text-zinc-500 mt-1 mb-6 text-sm">Start creating cinematic videos with AI</p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={onCreate}
                                        className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-full px-6 py-2.5 transition-transform hover:-translate-y-0.5 text-sm"
                                    >
                                        <Plus className="w-4 h-4" strokeWidth={3} />
                                        Create First Project
                                    </button>
                                    <button
                                        onClick={onRefresh}
                                        className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-full px-5 py-2.5 text-sm font-semibold transition-all"
                                    >
                                        Check Cloud Projects
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-zinc-500 text-sm">No projects match "{search}"</p>
                            </div>
                        )}
                    </div>

                    {/* Right: Preview Panel */}
                    <div className="lg:col-span-4" data-testid="project-preview-panel">
                        <div className="lg:sticky lg:top-8 rounded-2xl backdrop-blur-xl border border-white/10 overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.4)]" style={{ background: 'rgba(15, 17, 21, 0.8)' }}>
                            {selectedProject ? (
                                <>
                                    <div className="h-44 overflow-hidden relative group/thumb cursor-pointer"
                                        onClick={() => thumbInputRef.current?.click()}
                                        title="Click to upload thumbnail"
                                    >
                                        <img
                                            src={selectedProject.thumbnail || getThumb(projects.indexOf(selectedProject))}
                                            alt={selectedProject.name}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] to-transparent" />
                                        {/* Upload overlay */}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                                            <div className="flex flex-col items-center gap-1.5">
                                                <ImagePlus className="w-6 h-6 text-white" />
                                                <span className="text-xs text-white font-medium">Upload Thumbnail</span>
                                            </div>
                                        </div>
                                        <input
                                            ref={thumbInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file || !selectedProject) return;
                                                const reader = new FileReader();
                                                reader.onload = () => {
                                                    if (onUpdateThumbnail) onUpdateThumbnail(selectedProject.id, reader.result);
                                                };
                                                reader.readAsDataURL(file);
                                                e.target.value = '';
                                            }}
                                        />
                                    </div>
                                    <div className="p-5">
                                        <h2 className="text-lg font-bold text-white mb-1">{selectedProject.name}</h2>
                                        <p className="text-xs text-zinc-500 mb-4">Last edited: {formatDate(selectedProject.lastModified)}</p>

                                        <div className="grid grid-cols-2 gap-3 mb-5">
                                            <div className="bg-white/5 rounded-xl px-3 py-2.5 border border-white/5">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Credits Used</p>
                                                <p className="text-sm text-violet-400 font-bold mt-0.5">{(selectedProject.creditsUsed || 0).toLocaleString()}</p>
                                            </div>
                                            <div className="bg-white/5 rounded-xl px-3 py-2.5 border border-white/5">
                                                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Updated</p>
                                                <p className="text-xs text-zinc-300 mt-0.5">{formatRelative(selectedProject.lastModified)}</p>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => onLoad(selectedProject.id)}
                                            className="w-full flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-full py-3 transition-transform hover:-translate-y-0.5 text-sm mb-3"
                                            data-testid="open-project-btn"
                                        >
                                            <Clapperboard className="w-4 h-4" />
                                            Open Studio
                                        </button>
                                        {confirmDeleteId === selectedProject.id ? (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => { onDelete(selectedProject.id); setSelectedId(null); setConfirmDeleteId(null); }}
                                                    className="flex-1 py-2 rounded-full text-sm font-semibold text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-colors"
                                                >
                                                    Confirm Delete
                                                </button>
                                                <button
                                                    onClick={() => setConfirmDeleteId(null)}
                                                    className="flex-1 py-2 rounded-full text-sm font-semibold text-zinc-400 border border-white/10 hover:bg-white/5 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setConfirmDeleteId(selectedProject.id)}
                                                className="w-full flex items-center justify-center gap-2 text-red-400/70 hover:text-red-400 text-sm py-2 transition-colors"
                                                data-testid="delete-project-btn"
                                            >
                                                <DeleteIcon className="w-4 h-4" />
                                                Delete Project
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="p-8 flex flex-col items-center text-center min-h-[300px] justify-center">
                                    <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                                        <Film className="w-7 h-7 text-zinc-600" />
                                    </div>
                                    <p className="text-sm text-zinc-500 font-medium">Select a project to preview</p>
                                    <p className="text-xs text-zinc-600 mt-1">Click on any project card</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
